import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  Chip,
  Button,
  IconButton,
  Stack,
  Alert,
  alpha,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { listJobs, downloadResult, type JobSummary } from "../services/api";
import { tokens } from "../theme";

export default function JobHistory() {
  const [jobs, setJobs] = useState<JobSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    try {
      const data = await listJobs();
      setJobs(data.jobs);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{
          bgcolor: alpha("#EF4444", 0.08),
          border: `1px solid ${alpha("#EF4444", 0.2)}`,
        }}
      >
        履歴の取得に失敗しました: {error}
      </Alert>
    );
  }

  if (jobs === null) return null;

  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: 11,
            fontWeight: 500,
            fontFamily: "inherit !important",
            flex: 1,
          }}
        >
          履歴 ({jobs.length})
        </Typography>
        <IconButton size="small" onClick={refresh} sx={{ color: "text.secondary" }}>
          <RefreshIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>

      {jobs.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            backgroundColor: "transparent",
            borderStyle: "dashed",
          }}
        >
          <InboxOutlinedIcon
            sx={{ fontSize: 28, color: "text.disabled", mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            まだジョブはありません
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: 0, overflow: "hidden" }}>
          <Stack divider={<Box sx={{ borderBottom: `1px solid ${tokens.border}` }} />}>
            {jobs.map((job) => (
              <Box
                key={job.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1, md: 2 },
                  px: { xs: 2, md: 3 },
                  py: { xs: 1.5, md: 2 },
                  transition: "background-color 150ms",
                  "&:hover": { backgroundColor: alpha("#fff", 0.02) },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily:
                          '"JetBrains Mono", "Geist Mono", monospace !important',
                        color: "text.primary",
                        fontSize: 12,
                      }}
                    >
                      {job.id.slice(0, 8)}
                    </Typography>
                    <Chip
                      label={jobStatusLabel(job.status)}
                      size="small"
                      color={statusColor(job.status)}
                      variant={job.status === "completed" ? "filled" : "outlined"}
                    />
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {job.total_files} 枚 · {formatLang(job.target_lang)} · {formatDate(job.created_at)}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                  {job.status === "completed" && job.result_r2_key && (
                    <Button
                      onClick={async () => {
                        try {
                          await downloadResult(job.id);
                        } catch (err) {
                          setError(err instanceof Error ? err.message : String(err));
                        }
                      }}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: 12,
                        py: 0.5,
                        minWidth: { xs: 36, sm: 56 },
                        px: { xs: 1, sm: 1.5 },
                      }}
                    >
                      <DownloadIcon sx={{ fontSize: 14, mr: { xs: 0, sm: 0.5 } }} />
                      <Box sx={{ display: { xs: "none", sm: "inline" } }}>DL</Box>
                    </Button>
                  )}
                  <Button
                    component={RouterLink}
                    to={`/jobs/${job.id}`}
                    size="small"
                    sx={{
                      fontSize: 12,
                      py: 0.5,
                      minWidth: 36,
                      px: 1,
                      color: "text.secondary",
                      "&:hover": { color: "text.primary" },
                    }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </Button>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );
}

function jobStatusLabel(s: string): string {
  switch (s) {
    case "created":
      return "未開始";
    case "processing":
      return "処理中";
    case "completed":
      return "完了";
    case "failed":
      return "失敗";
    default:
      return s;
  }
}

function statusColor(
  s: string,
): "default" | "success" | "error" | "warning" | "primary" {
  switch (s) {
    case "completed":
      return "success";
    case "failed":
      return "error";
    case "processing":
      return "warning";
    default:
      return "default";
  }
}

function formatLang(target: string | null): string {
  if (!target || target === "none") return "翻訳なし";
  if (target === "ja") return "日本語";
  if (target === "en") return "英語";
  return target;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso.replace(" ", "T") + "Z");
    return d.toLocaleString("ja-JP", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
